import React, { useState } from 'react';
import Indicator from '../../../../Components/Indicator/Indicator';
import IndicatorModal from '../../../../Containers/Dashboard/IndicatorModal';
import { DemeterDataFrequency, DemeterLeadingIndicatorModel } from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserCurrentMarket } from '../../../../Redux/Slices/UserSlice';
import IndicatorsManagementButton from '../../../Components/Form/Buttons/IndicatorsManagementButton/IndicatorsManagementButton';
import CardSkeleton from '../../../Components/Layout/CardSkeleton';
import ManageIndicatorsModal from '../../../Components/Modals/ProductsModal/ManageIndicatorsModal';
import { IndicatorsWidgetInfoInterface } from '../../../Core/Hooks/useLightstreamerMarketPricesHook';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import trackingService from '../../../Services/Tracking/TrackingService';
import IndicatorFrequencyNote from '../../Dashboard/Indicators/IndicatorFrequencyNote';
import useGlobalLeadingIndicators from '../Hooks/useGlobalLeadingIndicatorsHook';
import styles from './CustomDefaultDashboardComponents.module.scss';

interface ICustomDashboardIndicatorsProps {
    useDynamicHeight?: boolean;
}

const CustomDashboardIndicators: React.FC<ICustomDashboardIndicatorsProps> = ({ useDynamicHeight }) => {
    const [translations] = useLanguage();
    const market = useApplicationSelector(selectUserCurrentMarket);
    const { getIndicatorsForMarket } = useGlobalLeadingIndicators();
    const { allIndicators, userSelectedIndicators, indicatorsWidgetInfo, isLoading, updateIndicators } = getIndicatorsForMarket(market);
    const [showPopout, setShowPopout] = useState(false);
    const [modalSelectedIndicatorModel, setModalSelectedIndicatorModel] = useState<DemeterLeadingIndicatorModel>();
    const [productsModalOpen, setProductsModalOpen] = useState(false);

    const handleIndicatorUpdate = async (indicators: Array<DemeterLeadingIndicatorModel>) => {
        await updateIndicators(indicators);
    };

    const openModalAndSetProps = (indicator: DemeterLeadingIndicatorModel) => {
        setShowPopout(true);
        setModalSelectedIndicatorModel(indicator);
    };

    const getAsOfDateForIndicator = (indicator: IndicatorsWidgetInfoInterface) => {
        if (indicator.indicatorModel.dataFrequency === DemeterDataFrequency.Monthly) {
            return formattingService.toMonthYear(new Date(indicator.indicatorModel.asOfDate!));
        }

        if (!indicator.reutersInstrumentCode) {
            return formattingService.toLongDayMonthYear(new Date(indicator.indicatorModel.asOfDate!));
        }

        if (!indicator.isFuturesSettlement && indicator.latestPriceAsOfDate) {
            return formattingService.toTimestamp(new Date(indicator.latestPriceAsOfDate));
        }

        if (indicator.isFuturesSettlement && indicator.settlementPriceAsOfDate) {
            return formattingService.toLongDayMonthYear(new Date(indicator.settlementPriceAsOfDate));
        }

        return '';
    };

    const containerClassName = useDynamicHeight ? styles.leading_indicators_container_dynamic_height : styles.leading_indicators_container;
    const cardsContainerClassName = useDynamicHeight ? styles.leading_indicators_cards_container_dynamic_height : styles.leading_indicators_cards_container;

    return isLoading || !indicatorsWidgetInfo ? (
        <CardSkeleton />
    ) : (
        <div className={containerClassName}>
            <div data-testid="DashboardPageIndicators" className={styles.master_flex}>
                <div className={cardsContainerClassName}>
                    {indicatorsWidgetInfo.map((indicator) => (
                        <div
                            key={indicator.indicatorModel.demeterLeadingIndicatorGuid}
                            className={styles.leading_indicators_indicators_area}
                            onClick={() => {
                                openModalAndSetProps(indicator?.indicatorModel);
                                trackingService.track({
                                    eventCategory: 'Dashboard',
                                    eventAction: 'OpenLeadingIndicatorsModalClicked',
                                    eventValue: indicator.title,
                                });
                            }}
                            aria-hidden="true"
                        >
                            <Indicator
                                change={indicator.change !== null ? Math.round((indicator.change + Number.EPSILON) * 100) / 100 : indicator.change}
                                currency={indicator.currency}
                                indicatorNumber={indicator.value!}
                                isFuturesSettlement={indicator.isFuturesSettlement}
                                indicatorTitle={indicator.title}
                                displayDecimalPlacesMinimum={indicator.displayDecimalPlacesMinimum}
                                displayDecimalPlacesMaximum={indicator.displayDecimalPlacesMaximum}
                                dataFrequency={indicator.indicatorModel.dataFrequency}
                                asOfDate={getAsOfDateForIndicator(indicator)}
                                unitOfMeasure={indicator.indicatorModel.unitOfMeasure!}
                                previousValue={indicator.indicatorModel.previousValue!}
                                tableDefinitionType={indicator.indicatorModel.tableDefinitionType}
                            />
                        </div>
                    ))}
                    <div className={styles.leading_indicators_manage_indicators}>
                        <IndicatorsManagementButton
                            handleClick={() => setProductsModalOpen(true)}
                            tooltipText={translations.dashboard.indicators.addOrRemoveLeadingIndicators}
                            track={{ eventCategory: 'Dashboard', eventAction: 'OpenManageLeadingIndicatorsModalClicked' }}
                        />
                    </div>
                    <ManageIndicatorsModal
                        open={productsModalOpen}
                        title={`${translations.dashboard.indicators.manage} ${translations.dashboard.indicators.title}`}
                        selected={userSelectedIndicators}
                        indicators={allIndicators}
                        handleClose={() => setProductsModalOpen(false)}
                        handleChange={handleIndicatorUpdate}
                    />
                    <div>
                        <IndicatorModal indicatorModel={modalSelectedIndicatorModel} showPopout={showPopout} setShowPopout={setShowPopout} />
                    </div>
                </div>
            </div>
            <IndicatorFrequencyNote />
        </div>
    );
};

export default CustomDashboardIndicators;
